import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { navigate } from 'gatsby';
import React, { FC, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import { IButtonProps } from '@ui/atoms/TextButton';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import Checkbox from '@ui/forms/Checkbox';
import TextInput from '@ui/forms/TextInput';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';

import CREATE_CLAIMED_DEPENDENT from '@graphql/mutations/Dependent/CreateClaimedDependent.mutation.gql';

import { schema } from './DependentInvite.data';

export interface IDependentInvitePageContent {
	pageHeader: string;
	pageInfo: string;
	emailLabel: string;
	emailPlaceholder: string;
	overEighteenLabel: string;
	inviteLabel: string;
	dependentInviteError: string;
}

interface IDependentInviteProps extends IPageProps {
	pageContent: IDependentInvitePageContent;
}

const SpacedHeading = spaced(HeadingText);
const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const FullWidthForm = fullWidth('form');
const FullWidthButton = fullWidth<IButtonProps>(spaced<IButtonProps>(Button));

const DependentInvite: FC<IDependentInviteProps> = ({ pageContent }) => {
	const {
		pageHeader,
		pageInfo,
		emailLabel,
		emailPlaceholder,
		overEighteenLabel,
		inviteLabel,
		dependentInviteError,
	} = pageContent;

	const [showError, setShowError] = useState(false);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = form;
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const [inviteDependent] = useMutation(CREATE_CLAIMED_DEPENDENT, {
		client: graphQLv2Client,
	});

	const onSubmit = async (data: Record<string, unknown>) => {
		await inviteDependent({
			variables: {
				dependentEmail: data.email,
			},
		})
			.then(() => {
				navigate('/app/member/dependents');
			})
			.catch(() => {
				setShowError(true);
				return;
			});
	};

	return (
		<>
			<SpacedHeading marginBottom="md">{pageHeader}</SpacedHeading>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
				<SpacedColumn gap="lg" marginBottom="xxl">
					<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageInfo }} />
					<TextInput
						label={emailLabel}
						placeholder={emailPlaceholder}
						errorLabel={errors?.email && errors?.email?.message}
						{...register('email')}
					/>
					<Checkbox
						label={overEighteenLabel}
						textSize="xs"
						errorLabel={errors?.over18 && errors?.over18?.message}
						{...register('over18')}
					/>
				</SpacedColumn>
				<FullWidthButton label={inviteLabel} variant="secondary" type="submit" marginBottom="lg" />
				{showError && (
					<WYSIWYGContent
						color="red"
						tint="darken40"
						dangerouslySetInnerHTML={{ __html: dependentInviteError }}
					/>
				)}
			</FullWidthForm>
		</>
	);
};

export default DependentInvite;
